import React from 'react';

function JobModal({ job, onClose }) {
    const sections = [];
    if (job.whatYouDo) sections.push({ id: 'about', label: 'About the role' });
    if (job.whyJoin) sections.push({ id: 'why-join', label: 'Why you may want to join' });
    if (job.background) sections.push({ id: 'background', label: 'Background' });
    if (job.interviewProcess) sections.push({ id: 'interview', label: 'Interview process' });

    return (
        <div className="pt-24 pb-16 px-6 md:pt-32">
            <div>

                <div className="max-w-2xl mx-auto">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-sm text-[#777777] hover:text-[#FB651F] transition-colors mb-8"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Back to roles
                    </button>
                    <h3 className="text-xl font-medium text-[#1C1C1C] mb-2">{job.title}</h3>
                    <p className="text-base text-[#777777] mb-1">{job.location || 'San Francisco/London'}</p>
                    {job.salary && (
                        Array.isArray(job.salary) ? (
                            <div className="mb-6">
                                {job.salary.map((line, i) => (
                                    <p key={i} className="text-base text-[#777777]">{line}</p>
                                ))}
                            </div>
                        ) : (
                            <p className="text-base text-[#777777] mb-6">{job.salary}</p>
                        )
                    )}
                    {!job.salary && <div className="mb-5" />}
                    <p className="text-base text-[#1C1C1C] mb-6">To apply, email <a href="mailto:jobs@canopylabs.ai" className="text-[#FB651F] hover:underline">jobs@canopylabs.ai</a></p>

                    {/* Index */}
                    <nav className="mb-8 pb-8 border-b border-[#E5E5E5]">
                        <ul className="flex flex-wrap gap-x-6 gap-y-2">
                            {sections.map((s) => (
                                <li key={s.id}>
                                    <a
                                        href={`#${s.id}`}
                                        className="text-sm text-[#777777] hover:text-[#FB651F] transition-colors"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                    >
                                        {s.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    {/* About the role */}
                    {job.whatYouDo && (
                        <div id="about" className="mb-8 pb-8 border-b border-[#E5E5E5]">
                            <h4 className="text-base font-medium text-[#1C1C1C] mb-4">About the role</h4>
                            {Array.isArray(job.whatYouDo) ? (
                                job.whatYouDo.map((p, i) => (
                                    <p key={i} className="text-base leading-normal text-[#1C1C1C] font-normal mb-3 last:mb-0">{p}</p>
                                ))
                            ) : (
                                <p className="text-base leading-normal text-[#1C1C1C] font-normal">{job.whatYouDo}</p>
                            )}
                        </div>
                    )}

                    {/* Why you may want to join */}
                    {job.whyJoin && (
                        <div id="why-join" className="mb-8 pb-8 border-b border-[#E5E5E5]">
                            <h4 className="text-base font-medium text-[#1C1C1C] mb-4">Why you may want to join</h4>
                            <ul className="list-disc list-outside pl-5 space-y-2 text-base leading-normal text-[#1C1C1C] font-normal">
                                {job.whyJoin.map((item, i) => (
                                    <li key={i}>{item}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Background */}
                    {job.background && (
                        <div id="background" className="mb-8 pb-8 border-b border-[#E5E5E5]">
                            {job.backgroundTitle && (
                                <h4 className="text-base font-medium text-[#1C1C1C] mb-6">{job.backgroundTitle}</h4>
                            )}
                            <div className="space-y-8">
                                {job.background.map((item, i) => (
                                    <div key={i}>
                                        <h5 className="text-base font-medium text-[#1C1C1C] mb-3 italic">{item.heading}</h5>
                                        {item.content && item.content.map((p, j) => (
                                            <p key={j} className="text-base leading-normal text-[#1C1C1C] font-normal mb-3">{p}</p>
                                        ))}
                                        {item.list && (
                                            <ol className="list-decimal list-outside pl-5 space-y-2 text-base leading-normal text-[#1C1C1C] font-normal mb-3">
                                                {item.list.map((li, k) => (
                                                    <li key={k}>{li}</li>
                                                ))}
                                            </ol>
                                        )}
                                        {item.footer && (
                                            <p className="text-base leading-normal text-[#1C1C1C] font-normal">{item.footer}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Interview process */}
                    {job.interviewProcess && (
                        <div id="interview" className="mb-8">
                            <h4 className="text-base font-medium text-[#1C1C1C] mb-6">What is the interview process?</h4>
                            <div className="space-y-8">
                                {job.interviewProcess.map((step, i) => (
                                    <div key={i}>
                                        <h5 className="text-base font-medium text-[#1C1C1C] mb-3 italic">{step.title}</h5>
                                        {step.paragraphs && step.paragraphs.map((p, j) => {
                                            const text = typeof p === 'string' ? p : p.text;
                                            const link = typeof p === 'object' ? p.link : null;
                                            return (
                                                <React.Fragment key={j}>
                                                    <p className="text-base leading-normal text-[#1C1C1C] font-normal mb-3">{text}</p>
                                                    {link && (
                                                        <p className="text-base leading-normal text-[#1C1C1C] font-normal mb-3">
                                                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[#FB651F] hover:underline">{link.text}</a>
                                                        </p>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                        {step.list && (
                                            <ol className="list-decimal list-outside pl-5 space-y-2 text-base leading-normal text-[#1C1C1C] font-normal mb-3">
                                                {step.list.map((li, k) => (
                                                    <li key={k}>{li}</li>
                                                ))}
                                            </ol>
                                        )}
                                        {step.links && step.links.map((link, l) => (
                                            <p key={l} className="text-base leading-normal text-[#1C1C1C] font-normal mb-3">
                                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[#FB651F] hover:underline">{link.text}</a>
                                            </p>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

function JobItem({ title, location, salary, slug }) {
    return (
        <a
            href={`/jobs/${slug}`}
            onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, '', `/jobs/${slug}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="group flex items-start justify-between py-2 cursor-pointer block"
        >
            <div>
                <h3 className="text-base font-medium text-[#1C1C1C] group-hover:text-[#FB651F] transition-colors">{title}</h3>
                <p className="text-base text-[#777777] mt-1 font-light group-hover:text-[#FB651F] transition-colors">{location}</p>
                {salary && (
                    Array.isArray(salary) ? (
                        salary.map((line, i) => (
                            <p key={i} className="text-sm text-[#999999] mt-0.5 font-light group-hover:text-[#FB651F] transition-colors">{line}</p>
                        ))
                    ) : (
                        <p className="text-sm text-[#999999] mt-0.5 font-light group-hover:text-[#FB651F] transition-colors">{salary}</p>
                    )
                )}
            </div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#FB651F]">
                <path d="M2.4 12H20M20 12L13 5M20 12L13 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </a>
    );
}

const interviewProcess = [
    {
        title: 'Step 1: Introduction Call',
        paragraphs: [
            'Introduction call with a team member. The goal of this call is to:',
        ],
        list: [
            'Assess your fit from a skill perspective',
            'Assess your fit from a culture perspective',
            'Give you a chance to ask questions and find out more',
        ],
    },
    {
        title: 'Step 2: Technical Interview(s)',
        paragraphs: [
            'You will do a Maths and/or ML interview for the research role. You only need to do well enough on one of these interviews.',
            'Both interviews are ideally in person.',
            { text: 'The Maths interview is 90 minutes, with 45 minutes of independent work and 45 minutes of discussing the problems with a team member.', link: { text: 'Here is an example Maths interview', url: 'https://docs.google.com/document/d/1q-5kqJAoF1H8bO6lhQaS2zDhzmeAhwRgm8X5CuOp_9k/edit?usp=sharing' } },
            { text: 'The ML interview is 3 hours where you read a paper for 45 minutes, discuss the research with a team member for 15 minutes, then implement the paper in an adjacent task for around 105 minutes, followed by a short 15 minute review at the end.', link: { text: 'Here is an example ML interview', url: 'https://docs.google.com/document/d/17u4Ijwhza8kItk2u2Mp7_eI02K7urTxicOkfs6YFA3U/edit?usp=sharing' } },
            'We recommend you familiarise yourself with the Hugging Face transformers library and trainer before the ML interview.',
        ],
    },
    {
        title: 'Step 3: On-site',
        paragraphs: [
            'You will spend 2–5 days in person with the research team. Depending on the situation you would either work through a problem the team has already solved, work on a new problem with the team, or another activity that seems productive with two aims:',
        ],
        list: [
            'Helping the research team get a sense of working with you',
            'Helping you get a sense of working with the research team',
        ],
    },
];

const jobs = [
    {
        title: 'General Engineer', slug: 'general-engineer',
        whatYouDo: 'We are looking to work with engineers who are interested in building reliable systems for deploying and serving machine learning models in production. A large part of the work involves building infrastructure and services that support ML models in real-world applications.',
        whyJoin: [
            'Work directly on production systems powering state-of-the-art speech and avatar models',
            'Small team where every engineer has outsized impact on the product',
            'Rapid iteration cycles — your code ships to real users fast',
            'Deep exposure to cutting-edge ML research and infrastructure challenges',
        ],
        background: [
            {
                heading: 'Experience',
                content: [],
                list: [
                    'Experience working with modern ML tooling (e.g. Hugging Face, PyTorch, model serving frameworks)',
                    'Building production systems around ML models, including data pipelines and inference services',
                    'Familiarity with infrastructure and DevOps tooling such as Docker, Terraform, and CI/CD systems (e.g. Jenkins or similar)',
                ],
            },
            {
                heading: 'Typical work on the team',
                content: [],
                list: [
                    'Developing backend services for model inference (for example using FastAPI or similar frameworks)',
                    'Working with containerized environments and infrastructure automation',
                    'Building web interfaces or tools (e.g. React-based dashboards or internal tooling) to interact with deployed models',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: [
                    'Introduction call with a team member. The goal of this call is to:',
                ],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Technical Interview',
                paragraphs: [
                    'You will be asked to complete an engineering exercise related to your specialties and skillsets. This could be something like building a data pipeline or optimising a model.',
                    'Normally it will last about 3 hours:',
                ],
                list: [
                    '20 minutes of planning',
                    '10 minutes of discussion',
                    '2 hours 15 minutes of independent coding and implementation',
                    '15 minutes of review',
                ],
                links: [
                    { text: 'Here is an example coding interview for General Engineer - Data Engineer', url: 'https://docs.google.com/document/d/18NgLsFhWbLDmlZdbF6hjkEtz32Ic6tw5UX7jNwj6Gpc/edit?usp=sharing' },
                ],
            },
            {
                title: 'Step 3: On-site',
                paragraphs: [
                    'You will spend 2–5 days in person with the engineering team. Depending on the situation you would either work through a problem the team has already solved, build something new with the team, or another activity that seems productive with two aims:',
                ],
                list: [
                    'Helping the engineering team get a sense of working with you',
                    'Helping you get a sense of working with the engineering team',
                ],
            },
        ],
    },
    {
        title: 'Operations', slug: 'operations',
        whatYouDo: [
            'The Operations lead works directly with the CEO to build the operational backbone of a company in hypergrowth. You\'ll own systems, not just tasks. You\'ll have a seat at the table when strategy is discussed and your opinions will shape decisions.',
            'The right person for this role sees operational excellence as a craft — and treats every process they build as something that needs to hold up at 5x the current scale.',
        ],
        whyJoin: [
            'Trajectory: High performers will grow into a Chief of Staff role within 6–12 months',
            'Access: You\'ll work alongside the CEO daily and sit among world-class engineers building technology at the frontier of AI',
            'Equity: Meaningful stake in a company backed by the best investors in the world, at a stage where your impact directly moves the needle',
            'Optionality: Clear path to grow with the company as it scales',
            'Culture: War-room energy. We\'re building something from scratch and every day matters',
        ],
        background: [
            {
                heading: 'What you\'ll do',
                content: [],
                list: [
                    'Design and implement internal operational systems from scratch — we\'re building the playbook, not following one',
                    'Stand up and run hiring infrastructure: sourcing pipelines, interview coordination, candidate experience, onboarding flows',
                    'Coordinate with HR and Finance to manage payroll, contractor payments, vendor relationships, and compliance',
                    'Own the operational rhythm of the company: goal tracking, internal reporting',
                    'Identify bottlenecks in how the team works and fix them before they become problems',
                    'Act as a strategic thought partner to the CEO — not just executing decisions, but helping shape them',
                ],
            },
            {
                heading: 'Who you are',
                content: [],
                list: [
                    'You have exceptional attention to detail and take pride in getting the small things right',
                    'You\'ve thrived in high-intensity environments — consulting, investment banking, early-stage startups, or similar — and you\'re looking for something with more ownership and upside',
                    'You\'re a systems thinker who builds processes that scale, not band-aids that break',
                    'You\'re comfortable operating with ambiguity and moving fast without waiting for permission',
                    'You want to found a company one day and see this as the best possible training ground — learning how a venture-backed startup scales from 10 to 50+ while sitting next to the CEO',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: ['Introduction call with a team member. The goal of this call is to:'],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Spend Time with the Team',
                paragraphs: ['You will spend time with the team in person, working through real operational challenges and discussing how you would approach the role.'],
            },
        ],
    },
    {
        title: 'Data Ops', slug: 'data-ops',
        salary: 'Salary · $120k+ base with performance based bonus',
        whatYouDo: 'Data is the lifeblood of our models. In this role you will own the end-to-end data pipeline — sourcing, negotiating, and closing large-scale data contracts, then building the operational processes to get that data to the research team quickly and reliably. You will sit at the intersection of the research team, third-party data providers, and customers.',
        whyJoin: [
            'Own a critical function — the quality of our data directly determines the quality of our models',
            'Unusual mix of negotiation, operations, and technical work rarely found in one role',
            'Work with actors, directors, and creative professionals alongside a world-class research team',
            'High autonomy to design processes from scratch at a fast-growing company',
        ],
        background: [
            {
                heading: 'What you will do',
                content: [],
                list: [
                    'Negotiate and manage large data licensing and acquisition contracts',
                    'Design and run operational workflows that move data from source to training pipeline',
                    'Build lightweight internal tooling and automation to scale data operations',
                    'Coordinate across research, engineering, and external partners to unblock model development',
                    'Run processes for actors and directors involved in data capture sessions',
                ],
            },
            {
                heading: 'Typical backgrounds',
                content: [],
                list: [
                    'Top STEM programs',
                    'Investment banking or private equity',
                    'Management consulting (e.g. McKinsey, BCG, Bain)',
                    'Operations roles at high-growth startups',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: [
                    'Introduction call with a team member. The goal of this call is to:',
                ],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Spend Time with the Team',
                paragraphs: [
                    'You will spend time with the team in person, learning about the problems we face across data sourcing, pipeline operations, and partner coordination. You will share your own insights, discuss challenges, and work through real scenarios together.',
                ],
            },
            {
                title: 'Step 3: On-site',
                paragraphs: [
                    'You will spend 2–5 days in person with the data ops team. Depending on the situation you would either work through an operational challenge the team has already tackled, take on a new data pipeline or vendor coordination problem with the team, or another activity that seems productive with two aims:',
                ],
                list: [
                    'Helping the data ops team get a sense of working with you',
                    'Helping you get a sense of working with the data ops team',
                ],
            },
        ],
    },
    {
        title: 'Research', slug: 'research',
        whatYouDo: 'In this role you will create new learning techniques and architectures. You will take ideas from new papers and research and investigate promising ones.',
        whyJoin: [
            'Push the frontier of real-time speech and avatar generation',
            'Your research ships — models you build will be used by millions of people',
            'Work alongside a small, focused team with strong maths and ML backgrounds',
            'Freedom to explore novel architectures and training paradigms',
        ],
        background: [
            {
                heading: 'ML Masters/PhD',
                content: [
                    'The typical profiles of the team currently include:',
                ],
                list: [
                    'Working at top research labs (i.e. Harvard Robotics Research)',
                    'Published in top journals (i.e. NeurIPS)',
                ],
                footer: 'So you might fit in well if you find you have similar interests',
            },
            {
                heading: 'Maths/Physics',
                content: [
                    'The typical profiles of the research team currently are:',
                ],
                list: [
                    'Scored well on Math/Physics olympiads (for example 20+ BMO2, IPhO)',
                    'Done well in Maths/Physics degree programs (for example top 10/200)',
                    'Done impressive research at a good lab perhaps as a MsC or PhD',
                ],
                footer: 'So you might fit in well if you find you have similar interests',
            },
        ],
        backgroundTitle: 'Here are some typical profiles of people on the research team, if you feel like you would fit in or have anything additional to contribute we\'d love to chat!',
        interviewProcess,
    },
    {
        title: 'Creative Director', slug: 'creative-director',
        location: 'London',
        salary: 'Salary · $120k+ with performance based bonus',
        whatYouDo: [
            'We\'re building the next generation of voice and face AI, and we need someone with the creative instincts and directorial experience to draw out authentic, compelling performances from our talent. You\'ll lead our recording sessions — running auditions, coaching voice actors, and setting the creative standard for the performances that train our models.',
            'This is a role for someone who thinks first about the quality of a performance and second about the logistics around it. You\'ll work closely with a small, fast-moving team where your ear for nuance and your ability to communicate clearly with talent will matter more than your spreadsheets.',
        ],
        background: [
            {
                heading: 'What you\'ll do',
                content: [],
                list: [
                    'Direct voice talent and actors in recording sessions — drawing out emotionally specific, high-quality performances',
                    'Develop creative briefs and run auditions, identifying talent who can deliver across a wide range of styles and registers',
                    'Build and maintain a consistent creative vision for the voice performances that feed into our models',
                    'Manage sessions end-to-end, from talent outreach through to final delivery',
                    'Validate recorded data against production specs — catching quality problems before they become someone else\'s issue',
                    'Handle talent logistics: scheduling, contracts, payments, communications',
                ],
            },
            {
                heading: 'Suggested skills',
                content: [],
                list: [
                    'Demonstrable background in directing, acting, or voice coaching — you\'ve worked with performers and know how to get a great take',
                    'Deep understanding of performance craft — you can articulate what separates a good read from a great one',
                    'Experience running auditions and casting sessions',
                    'Confident, specific communicator with talent — you give direction that actually lands',
                    'Organised enough to manage scheduling, file delivery, and logistics without dropping things',
                    'Technically literate: comfortable with file specs and production pipelines',
                ],
            },
            {
                heading: 'Who you are',
                content: [
                    'We\'re a small team moving quickly. The right person for this role will be comfortable with ambiguity, proactive about solving problems, and genuinely interested in the work.',
                    'If that sounds like you, we\'d love to hear from you.',
                ],
            },
        ],
    },
    {
        title: 'Studio Manager', slug: 'studio-manager',
        location: 'London',
        salary: ['Salary · £40k', 'Hours · 9–6'],
        whatYouDo: [
            'We run a high-throughput recording studio capturing the voice and video data that trains our models. As Studio Manager, you are responsible for keeping the studio running smoothly at all times — a generalist operations role that requires someone calm, proactive, and comfortable dealing with whatever the day throws at them.',
        ],
        background: [
            {
                heading: 'What you\'ll do',
                content: [],
                list: [
                    'Run the studio day-to-day at consistently high session turnover',
                    'Deal with issues as they arise — equipment, talent, scheduling, logistics',
                    'Support actors when they arrive: onboarding, orientation, and getting them set up',
                    'Coordinate across Studio Coordinators and Monitors to keep sessions moving',
                    'Own the studio environment — cleanliness, equipment readiness, and general upkeep',
                ],
            },
            {
                heading: 'Suggested background',
                content: [],
                list: [
                    'Experience in operations, studio, or production management',
                    'Calm and decisive under pressure',
                    'Strong communicator — clear with talent and colleagues alike',
                    'Highly organised with a bias for action',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: ['Introduction call with a team member. The goal of this call is to:'],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Spend Time with the Team',
                paragraphs: ['You will spend time in person seeing how the studio runs and discussing how you would approach the role.'],
            },
        ],
    },
    {
        title: 'Studio Coordinator', slug: 'studio-coordinator',
        location: 'London',
        salary: ['Salary · £40k', 'Hours · 9–6'],
        whatYouDo: [
            'Our Studio Coordinators are responsible for getting each actor set up correctly in their booth before every session begins. You will ensure every recording starts on time and to spec.',
        ],
        background: [
            {
                heading: 'What you\'ll do',
                content: [],
                list: [
                    'Set up actors in booths at the start of each session',
                    'Adjust tripod position, check lighting, and verify audio and video quality',
                    'Keep track of session time and stop actors promptly when their slot is up',
                    'Ensure booths are reset and ready for the next actor',
                ],
            },
            {
                heading: 'Suggested background',
                content: [],
                list: [
                    'Comfortable with basic AV equipment — cameras, microphones, lighting',
                    'Punctual and precise — timekeeping matters in a high-turnover environment',
                    'Friendly and efficient with talent',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: ['Introduction call with a team member. The goal of this call is to:'],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Spend Time with the Team',
                paragraphs: ['You will spend time in person seeing how the studio runs and discussing how you would approach the role.'],
            },
        ],
    },
    {
        title: 'Studio Monitor', slug: 'studio-monitor',
        location: 'London',
        salary: ['Salary · £40k', 'Hours · 9–6'],
        whatYouDo: [
            'Studio Monitors are responsible for the quality of recordings while sessions are in progress. You will watch the feeds continuously and intervene the moment something is off.',
        ],
        background: [
            {
                heading: 'What you\'ll do',
                content: [],
                list: [
                    'Continuously monitor live recording feeds across all active booths',
                    'Check that every actor is in frame and meeting the required visual and audio standards',
                    'Go and correct actors directly when something is wrong — framing, positioning, performance quality',
                    'Flag and escalate any technical issues to the Studio Coordinator or Manager',
                ],
            },
            {
                heading: 'Suggested background',
                content: [],
                list: [
                    'Sharp eye for detail — you notice when something is slightly off',
                    'Comfortable watching multiple feeds simultaneously',
                    'Confident giving brief, clear direction to talent',
                    'Experience in video production, broadcast, or live event monitoring a plus',
                ],
            },
        ],
        interviewProcess: [
            {
                title: 'Step 1: Introduction Call',
                paragraphs: ['Introduction call with a team member. The goal of this call is to:'],
                list: [
                    'Assess your fit from a skill perspective',
                    'Assess your fit from a culture perspective',
                    'Give you a chance to ask questions and find out more',
                ],
            },
            {
                title: 'Step 2: Spend Time with the Team',
                paragraphs: ['You will spend time in person seeing how the studio runs and discussing how you would approach the role.'],
            },
        ],
    },
    {
        title: 'Brand/Media Director', slug: 'brand-media-director',
        location: 'London/SF',
        salary: 'Salary · $80–120k',
        whatYouDo: [
            'We are looking for a Brand/Media Director to shape how Orpheus shows up in the world. The core of the role is designing and executing our content strategy across social channels, building an audience, and creating the materials that help people understand and trust the product.',
            'You will work closely with the founders and the wider team to turn technical progress into compelling content — from launch videos and product demos to tutorials, blog posts, and community building.',
        ],
        whyJoin: [
            'Own the voice and public presence of a company working at the frontier of voice and face AI',
            'Work directly with a small team where brand decisions can quickly shape company trajectory',
            'Translate genuinely novel technical progress into stories the market will remember',
            'Build the media engine, not just the campaigns, at an early-stage company',
        ],
        background: [
            {
                heading: 'What matters',
                content: [],
                list: [
                    'Creative taste. Knows what good content looks and feels like. Has a point of view. Can point to work they\'ve made that proves it.',
                    'Audience-building. Has grown a following or helped grow one. Understands distribution, not just creation.',
                    'Output. Ships a lot, fast. Doesn\'t wait for permission or process.',
                    'Technical fluency. Can learn the product, use it, and show others how to use it well. Credible in technical conversations — doesn\'t need to be deep, needs to not be wrong.',
                ],
            },
            {
                heading: 'The work',
                content: [],
                list: [
                    'Launch videos for new models/languages, features, and partnerships',
                    'Social media — own the channels, post consistently, build the audience',
                    'Blogs, best-practice guides, and integration tutorials',
                    'Product demos and showcases — actually use Orpheus and show what it can do',
                    'Know the product cold. Know the competitors. Know why we\'re better and how to show it.',
                ],
            },
            {
                heading: 'Channels',
                content: [
                    'Twitter/X, LinkedIn, YouTube / short-form video, blog, and community — Discord, HuggingFace, GitHub.',
                ],
            },
        ],
    },
];

function JobsSection({ showViewAll = false }) {
    return (
        <section id="jobs" className="relative mb-16">
            <div className="md:absolute md:left-[calc(-50vw+22rem)] md:top-0 md:w-[calc(50vw-22rem)] md:text-center mb-6 md:mb-0">
                <h2 className="text-base font-medium text-[#777777] lowercase">jobs</h2>
            </div>
            <div className="space-y-8">
                {jobs.map((job) => (
                    <JobItem
                        key={job.slug}
                        title={job.title}
                        location={job.location || 'San Francisco/London'}
                        salary={job.salary}
                        slug={job.slug}
                    />
                ))}
            </div>
            {showViewAll && (
                <div className="mt-8">
                    <a
                        href="/jobs"
                        onClick={(e) => {
                            e.preventDefault();
                            window.history.pushState({}, '', '/jobs');
                            window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="text-sm text-[#777777] hover:text-[#FB651F] transition-colors flex items-center gap-2"
                    >
                        View all open roles
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.4 12H20M20 12L13 5M20 12L13 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                </div>
            )}
        </section>
    );
}

export { jobs, JobModal };
export default JobsSection;
